import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  MenuItem,
  InputAdornment,
  Pagination,
} from "@mui/material";
import { Edit, Delete, Search } from "@mui/icons-material";
import { db } from "../../firebase";
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc } from "firebase/firestore";

function Tripproducts() {
  const [products, setProducts] = useState([]);
  const [sizes, setSizes] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentId, setCurrentId] = useState(null);
  const [productName, setProductName] = useState("");
  const [rate, setRate] = useState("");
  const [sizeValue, setSizeValue] = useState("");
  const [gst, setGst] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [tripType, setTripType] = useState("Sale"); // default Sale
  
  const rowsPerPage = 10;

  const productsRef = collection(db, "trip_products");
  const sizesRef = collection(db, "product_sizes");

  // Fetch products
  const fetchProducts = async () => {
    const snap = await getDocs(productsRef);
    const data = snap.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
    setProducts(data);
    setFilteredProducts(data);
  };

  // Fetch sizes
  const fetchSizes = async () => {
    const snap = await getDocs(sizesRef);
    const data = snap.docs.map((doc) => doc.data().size);
    setSizes(data);
  };

  useEffect(() => {
    fetchSizes();
    fetchProducts();
  }, []);

  // Search filter
  useEffect(() => {
    const filtered = products.filter(
      (item) =>
        item.productName.toLowerCase().includes(search.toLowerCase()) ||
        item.size.toLowerCase().includes(search.toLowerCase())
    );
    setFilteredProducts(filtered);
    setPage(1);
  }, [search, products]);

  const handleOpenDialog = (mode = false, id = null, product = {}) => {
    setEditMode(mode);
    setCurrentId(id);
    setProductName(product.productName || "");
    setRate(product.rate || "");
    setSizeValue(product.size || "");
    setGst(product.gst || "");
    setOpenDialog(true);
    setTripType(product.tripType || "Sale");
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setProductName("");
    setRate("");
    setSizeValue("");
    setGst("");
    setTripType("Sale");
    setCurrentId(null);
  };

  const handleAdd = async () => {
    if (!productName) return;
    await addDoc(productsRef, { productName, rate, size: sizeValue, gst, tripType  });
    fetchProducts();
    handleCloseDialog();
  };

  const handleUpdate = async () => {
    if (!productName || !currentId) return;
    const docRef = doc(db, "trip_products", currentId);
    await updateDoc(docRef, { productName, rate, size: sizeValue, gst, tripType  });
    fetchProducts();
    handleCloseDialog();
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this product?")) {
      await deleteDoc(doc(db, "trip_products", id));
      fetchProducts();
    }
  };

  const paginatedData = filteredProducts.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage
  );

  return (
    <Box sx={{ p: 1 }}>
      <Typography variant="h5" fontWeight={700} mb={2}>
        Trip Products
      </Typography>

      {/* Search + Add */}
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
        <TextField
          placeholder="Search by product or size"
          size="small"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
        />
        <Button variant="contained" onClick={() => handleOpenDialog(false)}>
          Add Product
        </Button>
      </Box>

      {/* Table */}
      <TableContainer component={Paper} sx={{ mb: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>#</TableCell>
              <TableCell>Product Name</TableCell>
              <TableCell>Rate</TableCell>
              <TableCell>Size</TableCell>
              <TableCell>GST (%)</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedData.map((item, index) => (
              <TableRow key={item.id}>
                <TableCell>{(page - 1) * rowsPerPage + index + 1}</TableCell>
                <TableCell>{item.productName}</TableCell>
                <TableCell>{item.rate}</TableCell>
                <TableCell>{item.size}</TableCell>
                <TableCell>{item.gst}</TableCell>
                <TableCell>{item.tripType || " "}</TableCell>
                <TableCell>
                  <IconButton color="primary" onClick={() => handleOpenDialog(true, item.id, item)}>
                    <Edit />
                  </IconButton>
                  <IconButton color="error" onClick={() => handleDelete(item.id)}>
                    <Delete />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {paginatedData.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  No Products Found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      <Pagination
        count={Math.ceil(filteredProducts.length / rowsPerPage)}
        page={page}
        onChange={(e, value) => setPage(value)}
        color="primary"
      />

      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog}>
        <DialogTitle>{editMode ? "Edit Product" : "Add Product"}</DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
          <TextField
            label="Product Name"
            value={productName}
            onChange={(e) => setProductName(e.target.value)}
            fullWidth
          />
          <TextField
            label="Rate"
            type="number"
            value={rate}
            onChange={(e) => setRate(e.target.value)}
            fullWidth
          />
          <TextField
            select
            label="Size"
            value={sizeValue}
            onChange={(e) => setSizeValue(e.target.value)}
            fullWidth
          >
            <MenuItem value="">Select Size</MenuItem>
            {sizes.map((s, i) => (
              <MenuItem key={i} value={s}>
                {s}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="GST (%)"
            type="number"
            value={gst}
            onChange={(e) => setGst(e.target.value)}
            fullWidth
          />
          <TextField
  select
  label="Trip Type"
  value={tripType}
  onChange={(e) => setTripType(e.target.value)}
  fullWidth
>
  <MenuItem value="Sale">Sale</MenuItem>
  <MenuItem value="Purchase">Purchase</MenuItem>
</TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button variant="contained" onClick={editMode ? handleUpdate : handleAdd}>
            {editMode ? "Update" : "Add"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Tripproducts;